import React, { useCallback, useMemo } from 'react';
import { useToast } from '@withjoy/joykit';
import { Skeleton } from '@shared/components/Skeleton';
import { useGetTagsByEventQuery, useCreateTagMutation, GetTagsByEventDocument } from '@graphql/generated';
import { useTranslation } from '@shared/core/i18n';
import { TagsInput } from './TagsInput';
import type { TagsInputContainerProps } from './TagsInputContainer.types';
import type { TagOption } from './TagsInput.types';

export const TagsInputContainer: React.FC<TagsInputContainerProps> = ({ eventId, value, onChange, ...rest }) => {
  const { tq } = useTranslation('tagsAdmin');
  const { toast } = useToast();

  const { data, loading } = useGetTagsByEventQuery({
    variables: { eventId },
    batchMode: 'fast',
  });

  const [createTagMutation, { loading: creating }] = useCreateTagMutation();

  const options: ReadonlyArray<TagOption> = useMemo(() => data?.getTagsByEvent ?? [], [data]);

  const handleCreateTag = useCallback(
    async (label: string) => {
      try {
        const result = await createTagMutation({
          variables: { eventId, label },
          refetchQueries: [{ query: GetTagsByEventDocument, variables: { eventId } }],
        });

        const newTag = result.data?.createTag;
        if (newTag && onChange) {
          // Use functional update to get latest value and avoid race condition
          onChange(prevValue => [...(prevValue ?? []), { id: newTag.id, label: newTag.label }]);
        }
        return newTag ? { id: newTag.id, label: newTag.label } : null;
      } catch {
        toast({ title: tq`Failed to create tag`, status: 'error' });
        return null;
      }
    },
    [createTagMutation, eventId, onChange, toast, tq],
  );

  if (loading) {
    return (
      <Skeleton
        width="100%"
        height={36}
        borderRadius="6px"
      />
    );
  }

  return (
    <TagsInput
      options={options}
      value={value}
      onChange={onChange}
      onCreateTag={handleCreateTag}
      isCreating={creating}
      {...rest}
    />
  );
};
